import type { WeaponId } from '../core/config';

// ---------------------------------------------------------------------------
// Weapon GLB model manifest — maps each weapon to its 3D model configuration.
// When GLB files are placed in /models/weapons/, the viewmodel system will
// load them and replace the procedural fallback mesh.
// ---------------------------------------------------------------------------

export interface WeaponModelConfig {
  /** Path to GLB file, e.g. '/models/weapons/ak47.glb' */
  glbPath: string;
  /** Uniform scale factor applied to the loaded model. */
  scale: number;
  /** [x, y, z] offset from viewmodel origin. */
  positionOffset: [number, number, number];
  /** Euler [x, y, z] rotation offset in radians. */
  rotationOffset: [number, number, number];
  /** Named animation clips expected inside the GLB. */
  animations?: {
    fire?: string;
    reload?: string;
    draw?: string;
    holster?: string;
    inspect?: string;
    idle?: string;
  };
  /**
   * Loading priority:
   * - 'blocking': loaded during game init, awaited before ready (primary weapon + knife)
   * - 'eager': loaded during freeze time, non-blocking (secondary + grenades)
   * - 'lazy': loaded on first switchWeapon() call
   */
  priority: 'blocking' | 'eager' | 'lazy';
}

/**
 * Weapon model configurations. Each weapon maps to its GLB path and transform
 * offsets. Initially all entries exist but GLB files may not be present yet --
 * the viewmodel system gracefully falls back to procedural meshes.
 *
 * Priority loading tiers (metadata consumed by viewmodel-loader.ts):
 * - 'blocking': Loaded during game init; awaited before the game is playable.
 *   Used for primary rifles and knife that the player spawns with.
 * - 'eager': Loaded asynchronously during freeze time (non-blocking).
 *   Used for pistols, AWP, and other commonly-bought secondaries.
 * - 'lazy': Loaded on-demand when the player first switches to the weapon.
 *   Used for less common weapons to reduce initial load time.
 *
 * The actual priority enforcement is in the viewmodel loader system
 * (weapon-viewmodel.ts / viewmodel-loader.ts) via getWeaponsByPriority().
 */
export const WEAPON_MODELS: Partial<Record<WeaponId, WeaponModelConfig>> = {
  // ── Blocking (primary rifles — loaded at init) ──
  // Quaternius "Ultimate Guns Pack" (CC0) — DRACO-compressed GLBs
  //
  // Offset notes:
  //   positionOffset is LOCAL to the weapon group (which sits at VIEW_OFFSET
  //   relative to vmCamera). Positive X = right, positive Y = up, negative Z
  //   = forward (toward screen center).
  //   Quaternius models face +Z; our camera looks along -Z, so a PI rotation
  //   on Y flips the barrel to point forward.
  //   Scale ~0.008-0.012 so rifle fills ~40% of screen width.

  // NOTE on `animations`: the current Quaternius GLBs ship WITHOUT clips, so
  // playWeaponAnimation silently no-ops at runtime (viewmodel.ts gates on
  // gltf.animations.length > 0 when building the mixer). The blocks below are
  // a clip-name contract for future Blender-authored rifle viewmodels — they
  // declare which named clips, once present in the GLB, map to which gameplay
  // events. Unit tests in __tests__/reload-animation.test.ts mock a GLB with
  // these clips to cover the playback machinery end-to-end.
  ak47: {
    glbPath: '/models/weapons/ak47.glb',
    scale: 0.010,
    positionOffset: [0.02, -0.04, -0.06],
    rotationOffset: [0, Math.PI, 0],
    animations: {
      fire: 'fire',
      reload: 'reload',
      draw: 'draw',
    },
    priority: 'blocking',
  },
  m4a4: {
    glbPath: '/models/weapons/m4a4.glb',
    scale: 0.010,
    positionOffset: [0.02, -0.03, -0.06],
    rotationOffset: [0, Math.PI, 0],
    animations: {
      fire: 'fire',
      reload: 'reload',
      draw: 'draw',
    },
    priority: 'blocking',
  },
  m4a1s: {
    glbPath: '/models/weapons/m4a1s.glb',
    scale: 0.010,
    positionOffset: [0.02, -0.03, -0.08],   // slightly more forward (suppressor extends barrel)
    rotationOffset: [0, Math.PI, 0],
    animations: {
      fire: 'fire',
      reload: 'reload',
      draw: 'draw',
    },
    priority: 'blocking',
  },

  // ── Lazy (loaded on first switchWeapon) ──
  famas: {
    glbPath: '/models/weapons/famas.glb',
    scale: 0.009,                            // FAMAS is slightly shorter / more compact
    positionOffset: [0.02, -0.03, -0.05],
    rotationOffset: [0, Math.PI, 0],
    animations: {
      fire: 'fire',
      reload: 'reload',
      draw: 'draw',
    },
    priority: 'lazy',
  },
  galil: {
    glbPath: '/models/weapons/galil.glb',
    scale: 0.010,
    positionOffset: [0.02, -0.04, -0.06],
    rotationOffset: [0, Math.PI, 0],
    animations: {
      fire: 'fire',
      reload: 'reload',
      draw: 'draw',
    },
    priority: 'lazy',
  },
  aug: {
    glbPath: '/models/weapons/aug.glb',
    scale: 0.009,                            // scoped rifle, slightly smaller scale for scope visibility
    positionOffset: [0.02, -0.02, -0.05],    // raised slightly to show scope
    rotationOffset: [0, Math.PI, 0],
    animations: {
      fire: 'fire',
      reload: 'reload',
      draw: 'draw',
    },
    priority: 'lazy',
  },
  sg553: {
    glbPath: '/models/weapons/sg553.glb',
    scale: 0.009,
    positionOffset: [0.02, -0.02, -0.05],
    rotationOffset: [0, Math.PI, 0],
    animations: {
      fire: 'fire',
      reload: 'reload',
      draw: 'draw',
    },
    priority: 'lazy',
  },

  // ── Blocking (knife — loaded at init) ──
  knife: {
    glbPath: '/models/weapons/knife.glb',
    scale: 0.012,
    positionOffset: [0.03, -0.03, -0.04],
    rotationOffset: [0, Math.PI, 0],
    animations: {
      fire: 'fire',
      draw: 'draw',
      holster: 'holster',
      inspect: 'inspect',
      idle: 'idle',
    },
    priority: 'blocking',
  },

  // ── Eager (pistols — loaded during freeze time) ──
  glock: {
    glbPath: '/models/weapons/glock.glb',
    scale: 0.010,
    positionOffset: [0.03, -0.03, -0.04],
    rotationOffset: [0, Math.PI, 0],
    animations: {
      fire: 'fire',
      reload: 'reload',
      draw: 'draw',
      inspect: 'inspect',
      idle: 'idle',
    },
    priority: 'eager',
  },
  usp: {
    glbPath: '/models/weapons/usp.glb',
    scale: 0.010,
    positionOffset: [0.03, -0.03, -0.04],
    rotationOffset: [0, Math.PI, 0],
    animations: {
      fire: 'fire',
      reload: 'reload',
      draw: 'draw',
      inspect: 'inspect',
      idle: 'idle',
    },
    priority: 'eager',
  },
  deagle: {
    glbPath: '/models/weapons/deagle.glb',
    scale: 0.011,
    positionOffset: [0.03, -0.03, -0.05],
    rotationOffset: [0, Math.PI, 0],
    animations: {
      fire: 'fire',
      reload: 'reload',
      draw: 'draw',
      inspect: 'inspect',
      idle: 'idle',
    },
    priority: 'eager',
  },
  awp: {
    glbPath: '/models/weapons/awp.glb',
    scale: 0.010,
    positionOffset: [0.02, -0.03, -0.08],
    rotationOffset: [0, Math.PI, 0],
    animations: {
      fire: 'fire',
      reload: 'reload',
      draw: 'draw',
      inspect: 'inspect',
      idle: 'idle',
    },
    priority: 'eager',
  },

  // ── Lazy (loaded on first switchWeapon) ──
  p250: {
    glbPath: '/models/weapons/p250.glb',
    scale: 0.010,
    positionOffset: [0.03, -0.03, -0.04],
    rotationOffset: [0, Math.PI, 0],
    animations: {
      fire: 'fire',
      reload: 'reload',
      draw: 'draw',
      inspect: 'inspect',
      idle: 'idle',
    },
    priority: 'lazy',
  },
  fiveseven: {
    glbPath: '/models/weapons/fiveseven.glb',
    scale: 0.010,
    positionOffset: [0.03, -0.03, -0.04],
    rotationOffset: [0, Math.PI, 0],
    animations: {
      fire: 'fire',
      reload: 'reload',
      draw: 'draw',
      inspect: 'inspect',
      idle: 'idle',
    },
    priority: 'lazy',
  },
  tec9: {
    glbPath: '/models/weapons/tec9.glb',
    scale: 0.010,
    positionOffset: [0.03, -0.03, -0.04],
    rotationOffset: [0, Math.PI, 0],
    animations: {
      fire: 'fire',
      reload: 'reload',
      draw: 'draw',
      inspect: 'inspect',
      idle: 'idle',
    },
    priority: 'lazy',
  },
  mp9: {
    glbPath: '/models/weapons/mp9.glb',
    scale: 0.010,
    positionOffset: [0.02, -0.03, -0.05],
    rotationOffset: [0, Math.PI, 0],
    animations: {
      fire: 'fire',
      reload: 'reload',
      draw: 'draw',
      inspect: 'inspect',
      idle: 'idle',
    },
    priority: 'lazy',
  },
  mac10: {
    glbPath: '/models/weapons/mac10.glb',
    scale: 0.010,
    positionOffset: [0.02, -0.03, -0.05],
    rotationOffset: [0, Math.PI, 0],
    animations: {
      fire: 'fire',
      reload: 'reload',
      draw: 'draw',
      inspect: 'inspect',
      idle: 'idle',
    },
    priority: 'lazy',
  },
  p90: {
    glbPath: '/models/weapons/p90.glb',
    scale: 0.009,
    positionOffset: [0.02, -0.03, -0.05],
    rotationOffset: [0, Math.PI, 0],
    animations: {
      fire: 'fire',
      reload: 'reload',
      draw: 'draw',
      inspect: 'inspect',
      idle: 'idle',
    },
    priority: 'lazy',
  },
  nova: {
    glbPath: '/models/weapons/nova.glb',
    scale: 0.010,
    positionOffset: [0.02, -0.04, -0.07],
    rotationOffset: [0, Math.PI, 0],
    animations: {
      fire: 'fire',
      reload: 'reload',
      draw: 'draw',
      inspect: 'inspect',
      idle: 'idle',
    },
    priority: 'lazy',
  },
  ssg08: {
    glbPath: '/models/weapons/ssg08.glb',
    scale: 0.010,
    positionOffset: [0.02, -0.03, -0.07],
    rotationOffset: [0, Math.PI, 0],
    animations: {
      fire: 'fire',
      reload: 'reload',
      draw: 'draw',
      inspect: 'inspect',
      idle: 'idle',
    },
    priority: 'lazy',
  },
};

/**
 * Get all weapon IDs that should be loaded at a given priority level.
 */
export function getWeaponsByPriority(priority: 'blocking' | 'eager' | 'lazy'): WeaponId[] {
  const result: WeaponId[] = [];
  for (const [id, cfg] of Object.entries(WEAPON_MODELS)) {
    if (cfg && cfg.priority === priority) {
      result.push(id as WeaponId);
    }
  }
  return result;
}
